import { useRef, useState } from 'react';
import { api } from '../../api/client';
import type { MessageDemande } from '../../types';
import { BoutonCopier } from './BoutonCopier';
import { EtatMessage } from './EtatMessage';
import { attendUneAction, ligneEtat, quand } from './format';
import { annoncerMiseAJourMessages } from './useCompteMessages';

const NB_VISIBLES = 3;

/**
 * Les e-mails aux remplaçant·e·s extérieur·e·s (gestion seulement) : un e-mail par personne, jamais d'e-mail commun.
 * « Préparer l'e-mail » ouvre la messagerie avec l'adresse, l'objet et le texte déjà remplis ; une fois parti,
 * « Marquer comme envoyé » (docs/DECISIONS.md §24).
 */
export function MessagesEmails({ messages, onChange }: { messages: MessageDemande[]; onChange: () => void }) {
  const [toutVoir, setToutVoir] = useState(false);
  const [enCours, setEnCours] = useState<number | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [annonce, setAnnonce] = useState('');
  const bloc = useRef<HTMLDivElement>(null);
  if (messages.length === 0) return null;

  const aEnvoyer = messages.filter(attendUneAction).length;
  const visibles = toutVoir ? messages : messages.slice(0, NB_VISIBLES);
  const premier = messages[0];

  async function marquer(m: MessageDemande) {
    setEnCours(m.id);
    setErreur(null);
    try {
      await api.marquerMessageEnvoye(m.id);
      setAnnonce(`E-mail pour ${m.destinataire_nom ?? m.destinataire ?? 'cette personne'} : marqué comme envoyé.`);
      annoncerMiseAJourMessages();
      onChange();
      bloc.current?.focus();
    } catch (err) {
      setErreur(err instanceof Error ? err.message : "L'enregistrement n'a pas abouti. Réessayez.");
      onChange();
    } finally {
      setEnCours(null);
    }
  }

  return (
    <div className="msg" ref={bloc} tabIndex={-1} data-etat={aEnvoyer > 0 ? 'a_envoyer' : messages.every((m) => m.statut === 'plus_utile') ? 'plus_utile' : 'envoye'}>
      <div className="msg-haut">
        <span className="msg-canal">E-mail</span>
        <b>{messages.length} e-mail{messages.length > 1 ? 's' : ''}, un par remplaçant·e extérieur·e</b>
        {aEnvoyer > 0
          ? <span className="badge msg-badge--a_envoyer">{aEnvoyer} à envoyer</span>
          : <EtatMessage statut={messages.every((m) => m.statut === 'plus_utile') ? 'plus_utile' : 'envoye'} />}
      </div>
      {aEnvoyer > 0 && <div className="msg-meta">Préparés le {quand(premier.created_at, false)} · à envoyer par la responsable ou les RH</div>}
      <ul className="msg-liste">
        {visibles.map((m) => {
          const attend = attendUneAction(m);
          const nom = m.destinataire_nom ?? m.destinataire ?? 'Adresse inconnue';
          return (
            <li key={m.id} className="msg-ligne">
              <span className="msg-ligne-qui">
                {nom}
                {m.destinataire_nom && m.destinataire && <small className="msg-meta"> · {m.destinataire}</small>}
              </span>
              <EtatMessage statut={m.statut} libelle={m.libelle_etat} />
              {!attend && <span className="msg-meta">{ligneEtat(m)}</span>}
              {attend && m.peut_marquer && (
                <span className="msg-actions">
                  {m.mailto
                    ? <a className="btn btn-sm btn-accent" href={m.mailto} aria-label={`Préparer l'e-mail pour ${nom}`}>{"Préparer l'e-mail"}</a>
                    : <span className="text-xs text-ink-3">Pas d'adresse e-mail enregistrée</span>}
                  <BoutonCopier texte={`${m.sujet ? `${m.sujet}\n\n` : ''}${m.corps}`} libelle="Copier" etiquette={`Copier l'e-mail pour ${nom}`} />
                  <button type="button" className="btn btn-sm btn-ghost" disabled={enCours === m.id} onClick={() => marquer(m)}
                    aria-label={`Marquer comme envoyé : e-mail pour ${nom}`}>
                    {enCours === m.id ? <span className="spinner spinner-sm" role="status" aria-label="Enregistrement en cours" /> : 'Marquer comme envoyé'}
                  </button>
                </span>
              )}
            </li>
          );
        })}
      </ul>
      {messages.length > NB_VISIBLES && (
        <button type="button" className="msg-lien" aria-expanded={toutVoir} onClick={() => setToutVoir((v) => !v)}>
          {toutVoir ? 'Réduire la liste des e-mails' : `Afficher les ${messages.length} e-mails`}
        </button>
      )}
      <span className="sr-only" role="status">{annonce}</span>
      {erreur && <div className="text-xs text-err" role="alert">{erreur}</div>}
    </div>
  );
}
